import MyLocationOutlined from "@mui/icons-material/MyLocationOutlined";
import { Button, Stack, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useAuth, useGeolocation } from "@bw/shared/hooks";
import { useMyProfile } from "../query/useMyProfile";
import type { NearbyCenter } from "../hooks/useNearbyCenter";

/**
 * Play's device-location opt-in and clear-location controls. Anonymous visitors
 * may opt in; authenticated profiles must resolve as not requiring consent.
 * Minors use the surrounding form's City filter without coordinates. An
 * unrestricted address picker is not a trustworthy coarse-city projection.
 *
 * The center is owned by the caller (controlled `center` + `onCenterChange`) so
 * the page can thread it into the search/drop-in read and persist it
 * client-side. Distance + sorting come from the BACKEND once a center is set;
 * this control never computes distance and shows none until a center exists.
 */
export interface NearbyControlProps {
  center: NearbyCenter | null;
  onCenterChange: (center: NearbyCenter) => void;
  hasLocation: boolean;
  onClearLocation: () => void;
  locationCopy?: string;
}

export function NearbyControl({
  center,
  onCenterChange,
  hasLocation,
  onClearLocation,
  locationCopy,
}: NearbyControlProps) {
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isSuccess: profileLoaded } = useMyProfile(
    Boolean(user),
  );
  const geo = useGeolocation();

  // Guests may explicitly opt into device location. For a signed-in identity,
  // withhold the precise control until profile consent state resolves; this
  // public surface no longer sits behind RequireProfile.
  const isUnconsentedMinor = profile?.requires_parental_consent === true;
  const signedInLocationAllowed =
    profileLoaded && profile?.requires_parental_consent === false;
  const showPrecise =
    !authLoading &&
    (user === null || signedInLocationAllowed) &&
    !isUnconsentedMinor;

  // When a precise fix is granted, lift it to the caller as the new center. A
  // ref guards against re-firing on every render once granted.
  const lastGrantedRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      showPrecise &&
      geo.status === "granted" &&
      geo.latitude !== undefined &&
      geo.longitude !== undefined
    ) {
      const key = `${geo.latitude},${geo.longitude}`;
      if (lastGrantedRef.current !== key) {
        lastGrantedRef.current = key;
        onCenterChange({ latitude: geo.latitude, longitude: geo.longitude });
      }
    }
  }, [geo.status, geo.latitude, geo.longitude, onCenterChange, showPrecise]);

  const geoErrored = geo.status === "denied" || geo.status === "unavailable";

  return (
    <Stack component="section" spacing={0.75} aria-label="Location">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ alignItems: { sm: "flex-start" } }}
      >
        {showPrecise ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<MyLocationOutlined />}
            onClick={() => {
              lastGrantedRef.current = null;
              geo.request();
            }}
            disabled={geo.status === "prompting"}
            aria-busy={geo.status === "prompting"}
            sx={{ flexShrink: 0, mt: { sm: 0.5 } }}
          >
            {geo.status === "prompting" ? "Locating…" : "Use my location"}
          </Button>
        ) : null}
        {hasLocation ? (
          <Button size="small" onClick={onClearLocation}>
            Clear location
          </Button>
        ) : null}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {locationCopy ?? (center
          ? center.label
            ? `Sorted by distance from ${center.label}`
            : "Sorted by distance from your location"
          : "Filter by city to choose where to play.")}
      </Typography>

      {showPrecise && geoErrored ? (
        <Typography variant="caption" color="warning.main" role="status">
          {geo.status === "denied"
            ? "Location access was blocked. Search a city or area instead."
            : "We couldn't get your location. Search a city or area instead."}
        </Typography>
      ) : null}
    </Stack>
  );
}
